import { ResumeService } from 'src/services/api/ResumeService';
import { defaultValues } from './constants';

// Transform new reference data API structure to match existing component expectations
function transformReferenceData(referenceData) {
  // Handle both response structures:
  // 1. Wrapped: { success, message, data: {...} }
  // 2. Direct: { skills: [...], certifications: [...], ... }
  let data;
  if (referenceData?.data) {
    data = referenceData.data;
  } else if (referenceData) {
    data = referenceData;
  } else {
    return {
      skills: [],
      certifications: [],
      fieldsofstudies: [],
      fieldsofstudieshsc: [],
      states: [],
      locations: {},
      preferedLocations: [],
      resumeUploadProcessLabels: [],
      industries: [],
      languages: [],
    };
  }

  // Build the form's `states` list + `locations` map ({ stateId: { cities } }).
  // Tolerant of several backend shapes:
  //  - nested: `states_and_cities` / `states` where each state carries `cities`
  //  - split : a top-level `states` list + a separate `cities` list whose items
  //            reference their state via state_id / stateId / state.
  const locations = {};
  const states = [];

  let rawStates = [];
  if (Array.isArray(data.states_and_cities)) {
    rawStates = data.states_and_cities;
  } else if (Array.isArray(data.states)) {
    rawStates = data.states;
  }

  // Group any standalone `cities` array onto its state id.
  const citiesByStateId = {};
  if (Array.isArray(data.cities)) {
    data.cities.forEach((c) => {
      const sid = c?.state_id ?? c?.stateId ?? c?.state ?? null;
      if (sid == null) return;
      if (!citiesByStateId[sid]) citiesByStateId[sid] = [];
      citiesByStateId[sid].push({
        id: c.id,
        name: c.name ?? c.city_name ?? '',
      });
    });
  }

  rawStates.forEach((state) => {
    states.push({ id: state.id, name: state.name ?? state.state_name ?? '' });
    const nestedCities = Array.isArray(state.cities)
      ? state.cities.map((c) => ({
          id: c.id,
          name: c.name ?? c.city_name ?? '',
        }))
      : null;
    locations[state.id] = {
      cities: nestedCities || citiesByStateId[state.id] || [],
    };
  });

  // fields_of_study is now a grouped list:
  //   [{ id, name: "Secondary Education", fields: [{id,name}] },
  //    { id, name: "Higher Education",    fields: [{id,name}] }]
  // Split the inner `fields` into HSC (10th/12th) vs higher (degree/etc.).
  // Falls back to a flat [{id,name}] list for older payloads.
  const fieldsOfStudyGroups = Array.isArray(data.fields_of_study)
    ? data.fields_of_study
    : [];
  // A group wraps its selectable options in a nested array; tolerate the common
  // key names so a group object never leaks into the dropdown as an option.
  const groupChildren = (g) => {
    const nested = g?.fields ?? g?.sub_fields ?? g?.children ?? g?.options;
    return Array.isArray(nested) ? nested : null;
  };
  const hasFosGroups = fieldsOfStudyGroups.some((g) => groupChildren(g));
  const pickGroupChildren = (matcher) =>
    groupChildren(
      fieldsOfStudyGroups.find((g) => matcher(g) && groupChildren(g))
    ) || [];
  const fieldsOfStudyHsc = hasFosGroups
    ? pickGroupChildren((g) => /secondary/i.test(g?.name || '') || g?.id === 1)
    : fieldsOfStudyGroups;
  const fieldsOfStudyHigher = hasFosGroups
    ? pickGroupChildren((g) => /higher/i.test(g?.name || '') || g?.id === 2)
    : fieldsOfStudyGroups;

  const resumeUploadProcessLabels = Array.isArray(
    data.resume_upload_process_labels
  )
    ? data.resume_upload_process_labels
        .map((row) => ({
          id:
            typeof row?.id === 'number'
              ? row.id
              : Number.parseInt(String(row?.id), 10),
          name: typeof row?.name === 'string' ? row.name : '',
        }))
        .filter((row) => Number.isFinite(row.id) && !Number.isNaN(row.id))
    : [];

  return {
    skills: data.skills || [],
    certifications: data.certifications || [],
    fieldsofstudies: fieldsOfStudyHigher,
    fieldsofstudieshsc: fieldsOfStudyHsc,
    states,
    locations,
    // Preferred-location options: the key was renamed `preferred_locations` →
    // `locations`; accept either.
    preferedLocations: data.locations || data.preferred_work_locations || [],
    resumeUploadProcessLabels,
    industries: data.industries || [],
    languages: data.languages || [],
  };
}

const createResumeApi = (config = {}) => {
  const {
    isProtected = false, // Flag to determine if it's protected route
    candidateId = null, // Optional candidate ID for protected route
    token = null, // Optional token for public route
  } = config;

  return {
    async fetchResumeData() {
      try {
        // Fetch reference data and profile data in parallel
        const [referenceResponse, profileResponse] = await Promise.all([
          isProtected
            ? ResumeService.getReferenceData()
            : ResumeService.getReferenceDataPublic(token),
          isProtected
            ? ResumeService.getProfile(candidateId)
            : ResumeService.getProfilePublic(token),
        ]);

        // Transform reference data
        const masterData = transformReferenceData(referenceResponse);

        // Both getProfile and getResumeProfile now return the data object directly
        // (they handle the new API response structure internally)
        const response = profileResponse;
        const candId = response.candidate_id;
        const profileData = response.profile_data;
        const personalInfo = profileData.personal_information;
        const address = personalInfo.address || {};
        const mappedEducations = profileData.educational_details?.length
          ? profileData.educational_details.map((edu) => {
              const isGPA = edu.score_type === 'GPA';
              let fieldsOfStudyArray = [];
              if (
                edu.qualification_type === 'TENTH' ||
                edu.qualification_type === 'TWELFTH'
              ) {
                fieldsOfStudyArray = masterData?.fieldsofstudieshsc || [];
              } else {
                fieldsOfStudyArray = masterData?.fieldsofstudies || [];
              }

              // Find field of study text from the master data
              const fieldOfStudyText =
                fieldsOfStudyArray.find(
                  (field) => field.id === edu.field_of_study_id
                )?.name || '';

              return {
                institution: edu.institution || '',
                qualification: edu.qualification_type || '',
                graduationYear: edu.graduation_year?.toString() || '',
                fieldsOfStudy: edu.field_of_study_id?.toString() || '',
                fieldOfStudyVal: fieldOfStudyText,
                calculatedScore: edu.calculated_score || '',
                gradeType:
                  !edu.score_type || edu.score_type === 'MARKS'
                    ? 'marks'
                    : 'gpa',
                // For marks
                maxMarks: !isGPA ? edu.max_score?.toString() || '' : '',
                earnedMarks: !isGPA ? edu.actual_score?.toString() || '' : '',
                // For GPA - Don't append '.0'
                maxGPA: isGPA ? edu.max_score?.toString() || '' : '',
                earnedGPA: isGPA ? edu.actual_score?.toString() || '' : '',
                board:
                  edu.qualification_type === 'TENTH' ||
                  edu.qualification_type === 'TWELFTH'
                    ? edu.board_type
                    : '',
                university:
                  edu.qualification_type !== 'TENTH' &&
                  edu.qualification_type !== 'TWELFTH'
                    ? edu.university
                    : '',
              };
            })
          : defaultValues.educations;

        const parseMonthYear = (value) => {
          if (!value || typeof value !== 'string') {
            return { month: '', year: '' };
          }
          if (value.includes('/')) {
            const [month, year] = value.split('/');
            return { month: month || '', year: year || '' };
          }
          if (value.includes('-')) {
            const [year, month] = value.split('-');
            return { month: month || '', year: year || '' };
          }
          return { month: '', year: '' };
        };

        const mappedData = {
          ...defaultValues,
          firstname: personalInfo.first_name || '',
          middlename: personalInfo.middle_name || '',
          lastname: personalInfo.last_name || '',
          candidateId: candId || '',
          email: personalInfo.email || '',
          contact: personalInfo.phone || '',
          dob: personalInfo.dob || '',
          gender: personalInfo.gender ?? '',
          fathersname: personalInfo.father_name || '',
          addressLine1: address.address_line_1 || '',
          street: address.street || '',
          city: address.city || '',
          state: address.state || '',
          zipCode: address.zip_code || '',
          educations: mappedEducations,
          currentCTC: personalInfo.current_ctc || '',
          expectedCTC: personalInfo.expected_ctc || '',
          // Preserve a 0 (Immediate) — `|| ''` would coerce it to empty.
          noticePeriod:
            personalInfo.notice_period != null
              ? personalInfo.notice_period
              : '',
          preferredLocations: Array.isArray(
            personalInfo.preferred_work_locations
          )
            ? personalInfo.preferred_work_locations.map((loc) =>
                typeof loc === 'number'
                  ? loc.toString()
                  : loc.id?.toString() || loc.toString()
              )
            : [],
          workExperiences: profileData.work_experiences?.length
            ? profileData.work_experiences.map((exp) => {
                const { month: fromMonth, year: fromYear } = parseMonthYear(
                  exp.from_date
                );
                const { month: toMonth, year: toYear } = parseMonthYear(
                  exp.to_date
                );
                const isCurrent = !!exp.is_current;

                return {
                  company: exp.company_name || '',
                  role: exp.role || '',
                  employment_period: {
                    startMonth: fromMonth || '',
                    startYear: fromYear || '',
                    endMonth: isCurrent ? '' : toMonth || '',
                    endYear: isCurrent ? '' : toYear || '',
                    present: isCurrent || (!toMonth && !toYear),
                  },
                };
              })
            : defaultValues.workExperiences,
          skills: (profileData.skills || []).map((s) => ({
            skill_id: s.id,
            ref_skill_id: null,
            skill_name: s.name,
          })),
          certifications: (profileData.certifications || []).map((c) => ({
            certification_id: c.id || null,
            ref_certification_id: null,
            name: c.name,
            issuingBody: c.issuing_body || null,
          })),
          // API returns these under `preferred_industries`; older payloads used
          // `preferred_industry_ids`. Accept either, and normalize numbers / {id} objects.
          // API returns these under `preferred_industries` as objects
          // ({ industry_id, name }); older payloads used `preferred_industry_ids`
          // (plain IDs). Accept either and normalize to numeric IDs.
          preferredIndustryIds: (
            profileData.job_preferences?.preferred_industries ??
            profileData.job_preferences?.preferred_industry_ids ??
            []
          )
            .map((item) =>
              item && typeof item === 'object'
                ? Number(item.industry_id ?? item.id)
                : Number(item)
            )
            .filter((n) => Number.isFinite(n)),
          workMode: profileData.job_preferences?.work_mode || [],
          companySizes: profileData.job_preferences?.company_size || [],
          openToRelocation:
            profileData.job_preferences?.open_to_relocation ?? null,
          languages: (profileData.languages || []).map((l) => {
            const master = (masterData?.languages || []).find(
              (m) => m.id === l.language_id
            );
            return {
              id: l.language_id,
              name: master?.name || '',
              proficiency: l.proficiency || 'CONVERSATIONAL',
              isPrimary: l.is_primary ?? false,
            };
          }),
          masterData: {
            skills: masterData?.skills || [],
            certifications: masterData?.certifications || [],
            fieldsofstudies: masterData?.fieldsofstudies || [],
            fieldsofstudieshsc: masterData?.fieldsofstudieshsc || [],
            states: masterData?.states || [],
            locations: masterData?.locations || [],
            preferedLocations: masterData?.preferedLocations || [],
            industries: masterData?.industries || [],
            languages: masterData?.languages || [],
          },
        };

        return mappedData;
      } catch (error) {
        console.error('Error fetching resume data:', error);
        throw error;
      }
    },

    async updateResume(formData, section) {
      try {
        if (!candidateId) {
          throw new Error('Candidate ID is required');
        }

        // Map section names to section_ref values
        const sectionRefMap = {
          contact: 'contact',
          preferences: 'compensation',
          job_preference: 'job_preference',
          personal: 'personal',
          skills: 'skills',
          education: 'education',
          experience: 'work_experience',
          certifications: 'certifications',
          summary: 'summary',
          languages: 'languages',
        };

        const sectionRef = sectionRefMap[section];
        if (!sectionRef) {
          throw new Error(`Unknown section: ${section}`);
        }

        let sectionData = {};

        // Transform data based on section
        switch (section) {
          case 'contact': {
            sectionData = {
              address_line_1: formData.addressLine1 || undefined,
              street: formData.street || undefined,
              city_id: formData.city ? parseInt(formData.city, 10) : undefined,
              state_id: formData.state
                ? parseInt(formData.state, 10)
                : undefined,
              zip_code: formData.zipCode || undefined,
              phone: formData.contact || undefined,
            };
            // Remove undefined values
            Object.keys(sectionData).forEach(
              (key) => sectionData[key] === undefined && delete sectionData[key]
            );
            break;
          }

          case 'preferences': {
            sectionData = {
              current_ctc: formData.currentCtc
                ? parseFloat(formData.currentCtc)
                : undefined,
              expected_ctc: formData.expectedCtc
                ? parseFloat(formData.expectedCtc)
                : undefined,
              // Guard on null/'' (not truthiness) so "Immediate" (0) is sent as
              // 0 rather than being dropped by the remove-undefined step below.
              notice_period:
                formData.noticePeriod != null && formData.noticePeriod !== ''
                  ? parseInt(formData.noticePeriod, 10)
                  : undefined,
              preferred_location_ids:
                Array.isArray(formData.preferredWorkLocations) &&
                formData.preferredWorkLocations.length > 0
                  ? formData.preferredWorkLocations.map((id) =>
                      parseInt(id, 10)
                    )
                  : undefined,
            };
            // Remove undefined values
            Object.keys(sectionData).forEach(
              (key) => sectionData[key] === undefined && delete sectionData[key]
            );
            break;
          }

          case 'personal': {
            sectionData = {
              father_name: formData.fathersname || undefined,
              dob: formData.dob || undefined, // Format: dd/MM/yyyy
              first_name: formData.firstname || undefined,
              middle_name: formData.middlename || undefined,
              last_name: formData.lastname || undefined,
              gender: formData.gender || undefined, // Optional field
            };
            // Remove undefined values
            Object.keys(sectionData).forEach(
              (key) => sectionData[key] === undefined && delete sectionData[key]
            );
            break;
          }

          case 'skills': {
            sectionData = {
              skills: formData.skills || [],
            };
            break;
          }

          case 'education': {
            if (!formData.educations || formData.educations.length === 0) {
              throw new Error('At least one education entry is required');
            }

            // Valid qualification types (backend enum values)
            const validQualificationTypes = [
              'TENTH',
              'TWELFTH',
              'DEGREE',
              'MASTERS',
            ];

            // Map score types to backend enum values
            const scoreTypeMap = {
              marks: 'MARKS',
              gpa: 'GPA',
            };

            // Valid board types (backend enum values)
            const validBoardTypes = ['STATE', 'CBSE', 'ICSE', 'IB', 'IGCSE'];

            sectionData = {
              educational_details: formData.educations.map((edu) => {
                // Ensure qualification type is uppercase and valid
                let qualificationType = edu.qualification?.toUpperCase();
                if (
                  !qualificationType ||
                  !validQualificationTypes.includes(qualificationType)
                ) {
                  // If not valid, default to DEGREE or throw error
                  qualificationType = 'DEGREE';
                }

                // Map score type to backend enum values
                const gradeTypeLower = edu.gradeType?.toLowerCase();
                let scoreType = scoreTypeMap[gradeTypeLower];
                if (!scoreType) {
                  // Default to MARKS if not gpa
                  scoreType = gradeTypeLower === 'gpa' ? 'GPA' : 'MARKS';
                }

                // Ensure board type is uppercase and valid (only for TENTH and TWELFTH)
                let boardType;
                if (
                  edu.qualification === 'TENTH' ||
                  edu.qualification === 'TWELFTH'
                ) {
                  if (edu.board) {
                    boardType = edu.board.toUpperCase();
                    // Validate board type, if not valid, use as-is (backend will validate)
                    if (!validBoardTypes.includes(boardType)) {
                      // If not in valid list, still send it (backend will handle validation)
                      // keep as-is; backend will validate
                    }
                  }
                }

                const educationEntry = {
                  institution: edu.institution,
                  graduation_year: parseInt(edu.graduationYear, 10),
                  qualification_type: qualificationType,
                  field_of_study_id:
                    edu.qualification === 'TENTH' || !edu.fieldsOfStudy
                      ? undefined
                      : parseInt(edu.fieldsOfStudy, 10),
                  score_type: scoreType,
                  max_score: parseFloat(
                    gradeTypeLower === 'gpa' ? edu.maxGPA : edu.maxMarks
                  ),
                  actual_score: parseFloat(
                    gradeTypeLower === 'gpa' ? edu.earnedGPA : edu.earnedMarks
                  ),
                  board_type: boardType,
                  university:
                    edu.qualification !== 'TENTH' &&
                    edu.qualification !== 'TWELFTH'
                      ? edu.university || undefined
                      : undefined,
                };

                // Remove undefined values
                Object.keys(educationEntry).forEach(
                  (key) =>
                    educationEntry[key] === undefined &&
                    delete educationEntry[key]
                );
                return educationEntry;
              }),
            };
            break;
          }

          case 'experience': {
            if (
              !formData.workExperiences ||
              formData.workExperiences.length === 0
            ) {
              throw new Error('At least one work experience entry is required');
            }

            sectionData = {
              work_experiences: formData.workExperiences.map((exp) => {
                // Backend expects `MM/YYYY`; the picker only gives month + year.
                const toApiDate = (month, year) =>
                  month && year
                    ? `${String(month).padStart(2, '0')}/${year}`
                    : null;
                const fromDate = toApiDate(
                  exp.employment_period.startMonth,
                  exp.employment_period.startYear
                );
                const isCurrent = !!exp.employment_period.present;
                const toDate = isCurrent
                  ? null
                  : toApiDate(
                      exp.employment_period.endMonth,
                      exp.employment_period.endYear
                    );

                if (!isCurrent && !toDate) {
                  throw new Error(
                    'to_date is required when is_current is False'
                  );
                }

                return {
                  // Include the backend id when this row already exists so the
                  // API can match it for update/delete; omitted for new rows.
                  ...(exp.id != null ? { id: exp.id } : {}),
                  company_name: exp.company,
                  role: exp.role,
                  from_date: fromDate,
                  to_date: toDate,
                  is_current: isCurrent,
                  duties: Array.isArray(exp.duties)
                    ? exp.duties.map((d) => ({
                        ...(d?.id != null ? { id: d.id } : {}),
                        title: d?.title != null ? String(d.title) : '',
                        description:
                          d?.description != null ? String(d.description) : '',
                      }))
                    : [],
                };
              }),
            };
            break;
          }

          case 'certifications': {
            const certs = formData.certifications || [];
            sectionData = {
              certifications: certs,
              no_certifications: certs.length === 0,
            };
            break;
          }

          case 'summary': {
            sectionData = {
              summary_text: formData.professionalSummary || undefined,
            };
            // Remove undefined values
            Object.keys(sectionData).forEach(
              (key) => sectionData[key] === undefined && delete sectionData[key]
            );
            break;
          }

          case 'job_preference': {
            sectionData = {
              preferred_location_ids: Array.isArray(
                formData.preferredWorkLocations
              )
                ? formData.preferredWorkLocations
                    .map((id) => parseInt(id, 10))
                    .filter((id) => !Number.isNaN(id))
                : [],
              work_mode: Array.isArray(formData.workMode)
                ? formData.workMode
                : [],
              company_size: Array.isArray(formData.companySizes)
                ? formData.companySizes
                : [],
              open_to_relocation: formData.openToRelocation ?? false,
              // Single-select job type — send the option `value` (e.g. "Full-Time").
              employment_type: Array.isArray(formData.jobType)
                ? formData.jobType
                : [],
              // Backend reads these back under `preferred_industries` (see GET
              // mapping); send both keys so the save round-trips regardless.
              preferred_industries: Array.isArray(formData.preferredIndustries)
                ? formData.preferredIndustries
                    .map((id) => parseInt(id, 10))
                    .filter((id) => !Number.isNaN(id))
                : [],
              preferred_industry_ids: Array.isArray(
                formData.preferredIndustries
              )
                ? formData.preferredIndustries
                    .map((id) => parseInt(id, 10))
                    .filter((id) => !Number.isNaN(id))
                : [],
            };
            break;
          }

          case 'languages': {
            sectionData = {
              languages: Array.isArray(formData.languages)
                ? formData.languages.map((l) => ({
                    language_id: l.id,
                    proficiency: l.proficiency,
                    is_primary: l.isPrimary ?? false,
                  }))
                : [],
            };
            break;
          }

          default:
            throw new Error(`Unsupported section: ${section}`);
        }

        // Create payload with key and data
        const payload = {
          key: sectionRef,
          data: sectionData,
        };

        // Use the appropriate endpoint based on route type
        const response = isProtected
          ? await ResumeService.updateProfileSection(candidateId, payload)
          : await ResumeService.updateProfileSectionPublic(
              candidateId,
              payload,
              token
            );

        // Check if response has success property
        if (response.success === false) {
          throw new Error(
            response.message || 'Failed to update resume section'
          );
        }

        return response;
      } catch (error) {
        console.error('Error updating resume section:', error);
        throw error;
      }
    },

    async getSummary() {
      try {
        if (!isProtected || !candidateId) {
          throw new Error('Candidate ID is required for protected route');
        }
        const response = await ResumeService.getSummary(candidateId);
        return response;
      } catch (error) {
        console.error('Error fetching summary:', error);
        throw error;
      }
    },
  };
};

export default createResumeApi;
